'use client';
import dynamic from 'next/dynamic';
import { useTranslations } from 'next-intl';
import type React from 'react';
import { useEffect } from 'react';
import VideoLazyLoad from '@/app/components/shared/VideoLazyLoad';
import { Button } from '@/app/components/ui/button';
import { reportEvent } from '@/app/utils';
import { useRouter } from '@/i18n/routing';
import type { FeatureCarouselItem } from '../../_components/FeatureCarousel';
import ProductInputTool from '../../_components/ProductInputTool';
import { reportProductPageElementClick } from '../../_components/ProductInputTool/utils';

const FeatureCarousel = dynamic(() => import('../../_components/FeatureCarousel').then((mod) => mod.FeatureCarousel));
const ProductSteps = dynamic(() => import('@/app/components/shared/ProductSteps').then((mod) => mod.ProductSteps));
const CommonMistakesSection = dynamic(() => import('./CommonMistakesSection').then((mod) => mod.CommonMistakesSection));
const QuestionSection = dynamic(() => import('./QuestionSection').then((mod) => mod.QuestionSection));
const RelatedProductsSection = dynamic(() =>
  import('../../_components/RelatedProductsSection').then((mod) => mod.RelatedProductsSection),
);

export const ImageToVideo: React.FC = () => {
  const router = useRouter();
  const t = useTranslations('ai-image-to-video');

  useEffect(() => {
    reportEvent('feature_page_view', {
      from_page: 'image to video',
    });
  }, []);

  // 处理生成按钮点击
  const handleGenerateClick = () => {
    reportEvent('element_click', {
      element_name: 'generate-now',
      page: 'ai-image-to-video',
    });
    reportProductPageElementClick('generate now');
    router.push('/create/img2video');
  };

  const videoStyles = {
    styleSectionVideo: [
      {
        id: 'anime',
        title: t('styles.anime.title'),
        videoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/anime-97bf351e-7f0ac56f.mp4',
        mobileVideoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/anime-97bf351e-6e3bb09f.mp4',
        poster: 'https://image01.cf.vidu.studio/vidu/media-asset/anime-831e1c7f.webp',
      },
      {
        id: 'realistic',
        title: t('styles.realistic.title'),
        videoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/real-50b872e0-117e28fd.mp4',
        mobileVideoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/real-50b872e0-272baf9f.mp4',
        poster: 'https://image01.cf.vidu.studio/vidu/media-asset/real-cdf470eb.webp',
      },
      {
        id: 'illustration',
        title: t('styles.illustration.title'),
        videoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/illustration-36781b4d-03d080fc.mp4',
        mobileVideoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/illustration-36781b4d-664233be.mp4',
        poster: 'https://image01.cf.vidu.studio/vidu/media-asset/illustration-639e0a7d.webp',
      },
      {
        id: 'surreal',
        title: t('styles.surreal.title'),
        videoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/surreal-fd958d89-2ae6d7b9.mp4',
        mobileVideoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/surreal-fd958d89-61944d17.mp4',
        poster: 'https://image01.cf.vidu.studio/vidu/media-asset/surreal-8487091a.webp',
      },
    ],
  };

  // 特性数据
  const features = [
    {
      id: 'feature-1',
      title: t('feature.firstAndLast.title'),
      description: t('feature.firstAndLast.description'),
      videoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/framefirst-d5d48c07.mp4',
      mobileVideoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/framefirst-d5d48c07-d22fd9dd.mp4',
      videoPoster: 'https://image01.cf.vidu.studio/vidu/media-asset/framefirst-f2f9cf3e.webp',
    },
  ];

  // 优势数据
  const advantages: FeatureCarouselItem[] = [
    {
      id: 'advantage-1',
      title: t('advantage.cinematicQuality.title'),
      description: t('advantage.cinematicQuality.description'),
      videoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/adv1-999339a1.mp4',
      mobileVideoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/adv1-999339a1-6ab9900e.mp4',
      videoPoster: 'https://image01.cf.vidu.studio/vidu/media-asset/adv1-c91c6215.webp',
    },
    {
      id: 'advantage-2',
      title: t('advantage.diverseStyles.title'),
      description: t('advantage.diverseStyles.description'),
      videoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/adv2-392d5f31.mp4',
      mobileVideoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/adv2-392d5f31-be3f5a63.mp4',
      videoPoster: 'https://image01.cf.vidu.studio/vidu/media-asset/adv2-ee2c88bb.webp',
    },
    {
      id: 'advantage-3',
      title: t('advantage.expressiveMotion.title'),
      description: t('advantage.expressiveMotion.description'),
      videoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/adv3-e9093dce.mp4',
      mobileVideoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/adv3-e9093dce-3b04240b.mp4',
      videoPoster: 'https://image01.cf.vidu.studio/vidu/media-asset/adv3-374ba2a8.webp',
    },
  ];

  // 使用场景数据
  const useCases = [
    {
      id: 'usecase-1',
      title: t('useCase.professionalShorts.title'),
      description: t('useCase.professionalShorts.description'),
      videoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/case1-c56ffa44-50e65d92.mp4',
      mobileVideoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/case1-c56ffa44-9969db5c.mp4',
      videoPoster: 'https://image01.cf.vidu.studio/vidu/media-asset/case1-d7a0c865.webp',
    },
    {
      id: 'usecase-2',
      title: t('useCase.productVideos.title'),
      description: t('useCase.productVideos.description'),
      videoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/case2-9d3e804c-9e87eb1a.mp4',
      mobileVideoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/case2-9d3e804c-26a210d9.mp4',
      videoPoster: 'https://image01.cf.vidu.studio/vidu/media-asset/case2-612f94e3.webp',
    },
    {
      id: 'usecase-3',
      title: t('useCase.memories.title'),
      description: t('useCase.memories.description'),
      videoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/case3-19d31ea9.mp4',
      mobileVideoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/case3-19d31ea9-07b33ea5.mp4',
      videoPoster: 'https://image01.cf.vidu.studio/vidu/media-asset/case3-b1056a84.webp',
    },
    {
      id: 'usecase-4',
      title: t('useCase.socialMedia.title'),
      description: t('useCase.socialMedia.description'),
      videoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/case4-33cfd56c-3ad52b76.mp4',
      mobileVideoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/case4-33cfd56c-2590ec46.mp4',
      videoPoster: 'https://image01.cf.vidu.studio/vidu/media-asset/case4-3515fb5d.webp',
    },
  ];

  // 步骤数据
  const stepItems = [
    {
      index: 1,
      title: t('step.step1.title'),
      description: t('step.step1.description'),
      image: 'https://image01.cf.vidu.studio/vidu/media-asset/step1-aa4ec98d.webp',
      alt: 'How to convert image to video with Vidu — upload image',
    },
    {
      index: 2,
      title: t('step.step2.title'),
      description: t('step.step2.description'),
      image: 'https://image01.cf.vidu.studio/vidu/media-asset/step2-e639e1f8.webp',
      alt: 'How to convert image to video with Vidu — adjust settings',
    },
    {
      index: 3,
      title: t('step.step3.title'),
      description: t('step.step3.description'),
      image: 'https://image01.cf.vidu.studio/vidu/media-asset/step3-3270cdec.webp',
      alt: 'How to convert image to video with Vidu — generate and download',
    },
  ];

  return (
    <div className="flex min-h-screen w-full flex-col bg-black text-white">
      <section className="pt-[164px] sm:pt-16">
        <div className="mx-auto flex flex-col items-center px-4">
          <div className="w-full max-w-[57vw] text-center">
            <h1 className="mb-4 font-semibold text-5xl leading-tight sm:text-4xl">{t('title')}</h1>
            <p className="mb-12 font-normal text-xl sm:mb-6 sm:text-base">{t('subtitle')}</p>
          </div>
          <div className="mx-auto min-h-[227px] w-full max-w-[57vw] md:max-w-full">
            <ProductInputTool />
          </div>
          <div className="mt-12 w-[74vw] overflow-hidden rounded-xl sm:w-full">
            <VideoLazyLoad
              videoSrc="https://image01.cf.vidu.studio/vidu/media-asset/banner-374a8e61.mp4"
              mobileVideoSrc="https://image01.cf.vidu.studio/vidu/media-asset/banner-374a8e61-a0ac9604.mp4"
              poster="https://image01.cf.vidu.studio/vidu/media-asset/banner-486db1f7.webp"
              priority={true}
              alt="Vidu AI image to video generator — animate photo example"
            />
          </div>
        </div>
      </section>

      {/* First & Last Frame Feature Section */}
      <section className="pt-[120px] sm:pt-10">
        <div className="mx-auto flex flex-col items-center px-4">
          <div className="w-[58vw] text-center">
            <h2 className="mb-4 font-semibold text-[40px] leading-tight sm:text-[36px]">{features[0].title}</h2>
            <p className="font-normal text-xl sm:text-base">{features[0].description}</p>
          </div>
          <div className="mt-6">
            <Button
              className="h-[68px] min-w-[224px] rounded-[100px] bg-[#0086FF] px-10 py-6 font-medium text-white text-xl shadow-lg transition-all hover:shadow-xl sm:h-14 sm:w-[195px] sm:py-5 sm:text-base"
              onClick={handleGenerateClick}
            >
              {t('buttons.generateNow')}
            </Button>
          </div>
          <div className="mt-16 w-[74vw] overflow-hidden rounded-xl sm:w-full">
            <VideoLazyLoad
              videoSrc={features[0].videoSrc}
              mobileVideoSrc={features[0].mobileVideoSrc}
              poster={features[0].videoPoster}
              alt={`Vidu AI image to video generator — ${features[0].title}`}
            />
          </div>
        </div>
      </section>

      {/* Next-Level Image to Video Section */}
      <FeatureCarousel
        title={t('nextLevel.title')}
        description=""
        features={advantages}
        buttonText={t('buttons.generateNow')}
        onButtonClick={handleGenerateClick}
        className="relative"
      />

      {/* Various Styles Section */}
      <section className="pt-[120px] sm:pt-10">
        <div className="mx-auto flex flex-col items-center px-4">
          <div className="w-[58vw] text-center">
            <h2 className="mb-4 font-semibold text-[40px] leading-tight sm:text-[36px]">{t('styles.title')}</h2>
            <p className="mb-16 font-normal text-xl sm:text-base">{t('styles.description')}</p>
          </div>

          <div className="grid w-[74vw] grid-cols-2 gap-6 sm:w-full sm:grid-cols-1">
            {videoStyles.styleSectionVideo.map((style) => (
              <div key={style.id} className="group relative overflow-hidden rounded-xl">
                <div className="aspect-video w-full overflow-hidden">
                  <VideoLazyLoad
                    videoSrc={style.videoSrc}
                    mobileVideoSrc={style.mobileVideoSrc}
                    poster={style.poster}
                    className="h-full w-full cursor-pointer object-cover transition-all duration-500"
                    hoverPlay={true}
                    alt={`Vidu AI image to video generator — ${style.title} style`}
                  />
                </div>
                <div className="mt-4 text-center font-medium text-lg text-white">{style.title}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="pt-[120px] sm:pt-10">
        <div className="mx-auto flex flex-col items-center px-4">
          <div className="w-[58vw] text-center">
            <h2 className="mb-16 font-semibold text-[40px] leading-tight sm:text-[36px]">{t('useCase.title')}</h2>
          </div>

          {/* Use Cases Grid */}
          <div className="grid w-[74vw] grid-cols-1 gap-16 md:w-full">
            {useCases.map((useCase, index) => (
              <div key={useCase.id} className="flex flex-row items-center gap-20 sm:flex-col sm:gap-16 sm:text-center">
                <div className={`flex-1 ${index % 2 === 1 ? 'order-2 sm:order-none' : ''}`}>
                  <h3 className="mb-6 font-semibold text-[32px] sm:text-2xl">{useCase.title}</h3>
                  <p className="mb-6 font-normal text-base">{useCase.description}</p>
                  <Button
                    className="rounded-[100px] border-2 border-[#0086FF] bg-transparent px-8 py-4 font-medium text-[#0086FF] text-sm shadow-lg transition-all hover:shadow-xl sm:py-4"
                    onClick={handleGenerateClick}
                  >
                    {t('buttons.generateNow')}
                  </Button>
                </div>
                <div className={`flex-1 ${index % 2 === 1 ? 'order-1 sm:order-none' : ''}`}>
                  <div className="overflow-hidden rounded-xl">
                    <VideoLazyLoad
                      videoSrc={useCase.videoSrc}
                      mobileVideoSrc={useCase.mobileVideoSrc}
                      poster={useCase.videoPoster}
                      alt={`AI animated video generated from static image using Vidu — ${useCase.title}`}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Common Image to Video Mistakes */}
      <CommonMistakesSection />

      {/* Steps Section */}
      <ProductSteps
        title={t('step.title')}
        items={stepItems}
        page="ai-image-to-video"
        theme="dark"
        redirectUrl="/create/img2video"
      />

      {/* Related Products Section */}
      <RelatedProductsSection currentSlug="ai-image-to-video" />

      {/* FAQ Section */}
      <QuestionSection />

      {/* CTA Banner */}
      <section className="relative mb-[120px] h-[433px] w-full sm:h-[219px]">
        {/* 视频背景 */}
        <div className="absolute inset-0 z-0 overflow-hidden">
          <VideoLazyLoad
            videoSrc="https://image01.cf.vidu.studio/vidu/media-asset/cta-6b2fe57b.mp4"
            poster="https://image01.cf.vidu.studio/vidu/media-asset/cta-bd625007.webp"
            className="h-full w-full object-cover"
            alt="AI animated video generated from static image using Vidu"
          />
        </div>

        {/* 内容覆盖层 */}
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/50">
          <div className="mx-auto max-w-4xl px-4 text-center sm:px-8">
            <h2 className="mb-8 font-bold text-[42px] sm:font-semibold sm:text-[32px]">{t('cta.ctaTitle')}</h2>
            <Button
              className="h-[60px] min-w-[224px] rounded-[100px] bg-[#0086FF] px-10 py-5 font-medium text-white text-xl shadow-lg transition-all hover:shadow-xl sm:h-12 sm:w-[195px] sm:py-4 sm:text-base"
              onClick={handleGenerateClick}
            >
              {t('buttons.generateNow')}
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};
